/**
 * THE PAGE STRIPE SENDS HER BACK TO, at `/payments/return`, the one address home gives ops as
 * `return_url`, with nothing of hers in it (the seam, l.641-646 and l.677-687 at b727ec1). Built
 * from `faithfully-kept-business/design/home/Payments.dc.html`, its third frame, "Back in home":
 * "Payments" over "Thank you.", a callout with the check drawn on it, then To pay and Paid as
 * Payments draws them (`Payments.tsx`). The words are the seam's, `RETURN_PAGE_WORDS` in `words.ts`
 * (ops's `docs/seams/home.md` at 0b51e49, section 5, Q2), which also says why the artboard's own
 * callout lines are not drawn.
 *
 * - WHAT IT SAYS comes from the three words ops adds to the address (`lib/paying.ts`), in the seam's
 *   order. After a payment: "Thank you." as the heading and that it is on its way; unless she paid
 *   on the card page, that a bank payment takes days and how confirming her bank account starts it;
 *   after the bank page with her tick (`saves=account`), what saving her bank account does and how
 *   to stop it; after the card page or the office's with Stripe's box (`saves=box`), the same as an
 *   if; then that she can close the page. After Stripe's back arrow: that she has not paid yet as
 *   the heading, that no payment was made, and how to pay, in home's own line, "To pay, press Pay on
 *   the invoice below." (`HOME_HOW_TO_PAY`, section 11.4 at ops 7e2d7ae, ops #71 item 4), in place of
 *   ops's line about the office's message, which may not have reached her, wherever a card below
 *   offers Pay; where none does, ops's own line, since home's would point at a Pay that is not on
 *   the page (the review of 574a196). "Questions?" with the
 *   office's number, a link to call it, ends the callout either way. After the card page, that her
 *   card tick asks for too (ops #70), nothing of saving: its address carries no `saves`. With
 *   nothing it can read, it is Payments as ever.
 * - NEVER PAID HERE. It says a payment is on its way and never that it is recorded: the invoice
 *   reads paid only once ops's push says so (CLAUDE.md binding rule 1).
 * - THE INVOICE SHE LEFT TO PAY is noted on its way, in every tab of her browser, or after the back
 *   arrow forgotten as the one this tab left for, as soon as the page is opened and before anything
 *   is read, so it holds whether or not she is still signed in. Payments and the invoice's page, in
 *   any tab, then hold back its Pay (P35).
 * - BACK TO THE INVOICE SHE LEFT, after Stripe's back arrow. The Pay on the card of the invoice this
 *   tab left to pay, as noting the return gives it back, opens it on the way she left it from, the
 *   bank or the card, handed to the page in the history and never in the address (slice V3, after
 *   the walk was replayed, whose way back opened on Zelle; on the Pay since slice M2 of round four),
 *   as home's line says. "Back to your invoice" (`PAYMENTS.backToInvoice`), the link home drew under
 *   the callout before section 11.4, is gone wherever Payments is drawn, two ways back to one
 *   invoice being one too many beside a line that points at Pay; it is kept, as an outlined button,
 *   only where Payments cannot be read, where nothing is drawn below the callout and it is the one
 *   way back. None after a payment, which is on its way and has nothing to go back to, nor where the
 *   tab noted no invoice, as a tab that never left to pay, or a reload once the return has been read
 *   (P35). The owner's walk of 2026-10-16 met the seam's line with nothing beside it.
 * - WHAT NEEDS NO READ IS SAID ANYWAY. Where Payments cannot be read, the heading and the callout
 *   are drawn alone, so a failed read never reads as a failed payment; after Stripe's back arrow
 *   their line on how to pay is section 5's own there, as on the door, since no Pay is below it. A
 *   401 is the door and a 404 is Home, as on Payments.
 * - SIGNED OUT, THE SAME WORDS ABOVE THE SIGN-IN (ops's plan for clients reaching only home, section
 *   7, ask 3): with no session the door is drawn here, and it says the heading and the callout above
 *   its sign-in, section 5's own line on how to pay among them after Stripe's back arrow, since no
 *   Pay is below it there (`screens/Door.tsx`, `components/ReturnWords.tsx`).
 */
import { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { GHOST_BUTTON } from '../components/buttons.ts';
import type { Signed } from '../components/Guarded.tsx';
import { ReturnWords } from '../components/ReturnWords.tsx';
import { noteReturn, readReturn, type Back, type Left, type OpenOn } from '../lib/paying.ts';
import { invoicePath } from '../paths.ts';
import { PAYMENTS } from '../words.ts';
import { Entry } from './Entry.tsx';
import { PaymentsShown } from './Payments.tsx';

export function PaymentReturn() {
  const { search } = useLocation();
  // Noted once, when the page is opened, before Entry asks who is signed in. Noting the same return
  // twice is noting it once (`noteReturn`), so a second render changes nothing. What it gives back,
  // the invoice this tab left for and the way she left it from, is kept while the page is open.
  const [leftFor] = useState(() => noteReturn(readReturn(search)));
  const screen = useCallback(
    (signed: Signed) => <Returned signed={signed} leftFor={leftFor} />,
    [leftFor],
  );
  return <Entry screen={screen} />;
}

/**
 * The place, signed in: Payments, with what the address back says above To pay, and the invoice
 * this tab left to pay, where it noted one. Payments tells the words above it whether a card below
 * offers Pay, so home's line saying to press Pay on the invoice below stands only where one does;
 * where none does (nothing owed, every invoice owed on its way, no card owed offering Pay, or the
 * invoices still coming down) section 5's own line stands (the review of 574a196).
 */
function Returned({ signed, leftFor }: { readonly signed: Signed; readonly leftFor: Left | null }) {
  const back = readReturn(useLocation().search);
  // After Stripe's back arrow, the invoice she left, whose Pay opens it on the way she left from.
  const unpaidFor = back !== null && !back.paid ? leftFor : null;
  return (
    <PaymentsShown
      signed={signed}
      above={
        back === null
          ? undefined
          : (payBelow) => <BackFrom back={back} wayBack={null} noPayBelow={!payBelow} />
      }
      aboveAlone={
        back === null ? undefined : <BackFrom back={back} wayBack={unpaidFor} noPayBelow />
      }
      leftFor={unpaidFor}
    />
  );
}

/**
 * "Payments" over the heading, and the callout under it, as the artboard's third frame
 * (`components/ReturnWords.tsx`), home's own line on how to pay in section 5's `howToPay`'s place
 * after Stripe's back arrow (section 11.4); and, given `wayBack`, the invoice the tab left for after
 * Stripe's back arrow, home's outlined button back to it under the callout, which opens it on the
 * way she left it from: given only where Payments cannot be read, so no Pay is drawn below. Where no
 * Pay is drawn below, `noPayBelow`, there or where Payments draws none, the line on how to pay is
 * section 5's own, as on the door, since home's says to press a Pay that is not on the page
 * (section 11.4: "the Pay button is below it").
 */
function BackFrom({
  back,
  wayBack,
  noPayBelow = false,
}: {
  readonly back: Back;
  readonly wayBack: Left | null;
  /** No Pay is drawn below: Payments cannot be read, or draws no card that offers Pay. */
  readonly noPayBelow?: boolean;
}) {
  return (
    <>
      <ReturnWords back={back} below={noPayBelow ? 'nothing' : 'pay'} />
      {back.paid || wayBack === null ? null : (
        <Link
          to={invoicePath(wayBack.invoiceId)}
          state={
            (wayBack.way === undefined
              ? { pay: true }
              : { pay: true, way: wayBack.way }) satisfies OpenOn
          }
          className={GHOST_BUTTON}
        >
          {PAYMENTS.backToInvoice}
        </Link>
      )}
    </>
  );
}
